import React, { useState } from "react";
// Zajednički stilovi popup-a za uvoz (tabela, statusi, filteri)
import "../importStrukture/importStrukture.css";
import "./importKorisnika.css";
import Button from "../../elements/button";
import Loading from "../../loading";
import { statusClass } from "../importStrukture";
import {
  validateImportFileLocally,
  postImportFile,
  importErrorMessage,
  downloadImportReport,
} from "../../../utils/importApi";

const STATUS_FILTERS = ["Sve", "NOVO", "KONFLIKT", "GREŠKA"];

/**
 * Popup za uvoz korisničkih naloga iz Excela (samo Administrator).
 * Zahteva da je struktura prethodno uvezena: nalozi se vezuju za
 * zaposlene iz uvoza strukture. Privremene lozinke se prikazuju samo
 * ovde i u preuzetom izveštaju; nigde se ne čuvaju.
 */
export default function ImportKorisnika({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, rows, canCommit }
  const [filter, setFilter] = useState("Sve");
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null); // { created, users, summary, rows }

  const handleFile = (e) => {
    setError("");
    setPreview(null);
    setCreated(null);
    setFile(e.target.files?.[0] || null);
  };

  const handlePreview = async () => {
    const localError = validateImportFileLocally(file);
    if (localError) {
      setError(localError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/users/preview", file);
      setPreview(data);
      setFilter("Sve");
      setAcknowledged(false);
    } catch (err) {
      setPreview(null);
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/users", file);
      setCreated(data);
      onImported?.();
    } catch (err) {
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    try {
      await downloadImportReport({
        rows: created ? created.rows : preview.rows,
        summary: created ? created.summary : preview.summary,
        users: created ? created.users : undefined,
      });
    } catch (err) {
      setError(importErrorMessage(err));
    }
  };

  const handleCopy = async () => {
    const text = created.users
      .map((u) => `${u.email}: ${u.temporaryPassword}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setError("");
    } catch (e) {
      setError("Kopiranje nije uspelo; preuzmite izveštaj sa lozinkama.");
    }
  };

  // prikaz rezultata uvoza: nalozi sa privremenim lozinkama
  if (created) {
    return (
      <div className="loading">
        <div className="import-popup">
          <p className="import-popup-title">Nalozi su otvoreni</p>
          <p className="import-popup-passwords-warning">
            Otvoreno je {created.created} korisničkih naloga. Privremene lozinke
            se prikazuju samo sada: preuzmite izveštaj ili ih kopirajte, pošto
            se nigde drugde ne čuvaju. Korisnici menjaju lozinku pri prvoj
            prijavi (strana Promena lozinke).
          </p>
          <div className="import-popup-table-wrapper">
            <table className="import-popup-table">
              <thead>
                <tr>
                  <th>Imejl</th>
                  <th>Ime</th>
                  <th style={{ width: "130px" }}>Rola</th>
                  <th style={{ width: "160px" }}>Privremena lozinka</th>
                </tr>
              </thead>
              <tbody>
                {created.users.map((u) => (
                  <tr key={u.email}>
                    <td>{u.email}</td>
                    <td>{u.name}</td>
                    <td>{u.role}</td>
                    <td className="import-popup-password">{u.temporaryPassword}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="import-popup-actions">
            <Button
              title="Preuzmi izveštaj sa lozinkama (XLSX)"
              onClick={handleReport}
              style={{ width: "280px" }}
            />
            {created.users.length > 0 && (
              <Button
                title="Kopiraj lozinke"
                onClick={handleCopy}
                style={{ width: "160px" }}
              />
            )}
            <Button title="Zatvori" onClick={onClose} style={{ width: "140px" }} />
          </div>
        </div>
        <Loading isLoading={loading} />
      </div>
    );
  }

  const rows =
    preview &&
    (filter === "Sve"
      ? preview.rows
      : preview.rows.filter((r) => r.status === filter));
  const hasWarnings =
    preview && preview.rows.some((r) => r.status === "UPOZORENJE");
  const canImport = preview && preview.canCommit && (!hasWarnings || acknowledged);

  return (
    <div className="loading">
      <div className="import-popup">
        <p className="import-popup-title">Uvoz korisničkih naloga iz Excela</p>

        <div className="import-popup-file-row">
          <input type="file" accept=".xlsx" onChange={handleFile} />
          {file && <span className="import-popup-file-name">{file.name}</span>}
        </div>
        <p className="import-popup-note">
          Nalozi se otvaraju za zaposlene sa kolonom „Korisnički nalog = DA" u
          istom fajlu kojim se uvozi struktura. Struktura mora biti ranije
          uvezena, jer se svaki nalog vezuje za zaposlenog i njegovu jedinicu.
          Privremenu lozinku generiše sistem i prikazuje je samo jednom, posle
          uvoza.
        </p>
        {error && <p className="import-popup-error">{error}</p>}

        {preview && (
          <>
            <div className="import-popup-summary">
              <span>Nalozi: {preview.summary.users.new} novo</span>
              <span>Konflikti: {preview.summary.users.conflict}</span>
            </div>

            <div className="import-popup-filters">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={
                    filter === s
                      ? "import-popup-filter active"
                      : "import-popup-filter"
                  }
                  onClick={() => setFilter(s)}
                >
                  {s === "Sve"
                    ? `Sve (${preview.rows.length})`
                    : `${s} (${
                        preview.rows.filter((r) => r.status === s).length
                      })`}
                </button>
              ))}
            </div>

            <div className="import-popup-table-wrapper">
              <table className="import-popup-table">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>Red</th>
                    <th style={{ width: "160px" }}>Zaposleni</th>
                    <th style={{ width: "110px" }}>Status</th>
                    <th>Poruka</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.row}-${i}`}>
                      <td>{r.row}</td>
                      <td>{r.name}</td>
                      <td>
                        <span
                          className={`import-popup-status ${statusClass(r.status)}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasWarnings && (
              <label className="import-popup-checkbox">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                Pregledao sam upozorenja i potvrđujem uvoz.
              </label>
            )}
            {!preview.canCommit && (
              <p className="import-popup-error">
                Dok postoje redovi sa statusom GREŠKA uvoz nije moguć; najčešći
                razlog je da struktura još nije uvezena.
              </p>
            )}
          </>
        )}

        <div className="import-popup-actions">
          <Button
            title="Proveri"
            onClick={handlePreview}
            disabled={!file || loading}
            style={{ width: "140px" }}
          />
          <Button
            title="Uvezi"
            onClick={handleImport}
            disabled={!canImport || loading}
            style={{ width: "140px" }}
          />
          {preview && (
            <Button
              title="Izveštaj provere (XLSX)"
              onClick={handleReport}
              disabled={loading}
              style={{ width: "190px" }}
            />
          )}
          <Button title="Otkaži" onClick={onClose} style={{ width: "140px" }} />
        </div>
      </div>
      <Loading isLoading={loading} />
    </div>
  );
}
